import { useQuery } from "@tanstack/react-query";
import { storesService } from "@/services/storesService";
import type { NearbyStoreRequest } from "@/types/store.dto";
import {
  mapStoreListDtosToStorefront,
  mapStoreResponseDtoToStorefront,
} from "@/lib/mappers/storefront-mappers";
import { queryKeys } from "./keys";

export function useNearbyStores(request: NearbyStoreRequest | null) {
  return useQuery({
    queryKey: queryKeys.stores.nearby(request ?? { latitude: 0, longitude: 0 }),
    queryFn: () => storesService.getNearby(request!),
    enabled: request != null && request.latitude !== 0 && request.longitude !== 0,
  });
}

export function useStorefrontNearbyStores(request: NearbyStoreRequest | null) {
  return useQuery({
    queryKey: queryKeys.stores.nearby(request ?? { latitude: 0, longitude: 0 }),
    queryFn: async () => {
      const result = await storesService.getNearby(request!);
      return {
        ...result,
        items: mapStoreListDtosToStorefront(result.items),
      };
    },
    enabled: request != null && request.latitude !== 0 && request.longitude !== 0,
  });
}

export function useStoreById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.stores.detail(id),
    queryFn: () => storesService.getById(id),
    enabled: enabled && id > 0,
  });
}

export function useStorefrontStoreById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.stores.detail(id),
    queryFn: async () => {
      const store = await storesService.getById(id);
      return mapStoreResponseDtoToStorefront(store);
    },
    enabled: enabled && id > 0,
  });
}

export function useAllStores(page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.stores.all(page, pageSize),
    queryFn: async () => {
      const result = await storesService.getAll(page, pageSize);
      return {
        ...result,
        items: mapStoreListDtosToStorefront(result.items),
      };
    },
  });
}
